import { useTranslations } from "next-intl";
import type { ReactNode } from "react";
import type {
  ControllerRenderProps,
  FieldValues,
  Path,
  UseFormReturn,
} from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { cn } from "@/lib/utils";

const layouts = {
  default: {
    formItemClasses: "space-y-2",
    formCheckboxClasses:
      "border-muted-foreground ring-offset-background focus-visible:ring-ring data-[state=checked]:bg-brand-green2 data-[state=checked]:text-primary-foreground data-[state=checked]:border-transparent peer h-6 w-6 shrink-0 rounded-[4.5px] border focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
    formMessageClasses: "",
    formFieldLabelClasses: "text-md font-normal",
  },
  bigWhite: {
    formItemClasses: "flex items-center space-x-3 space-y-0",
    formCheckboxClasses: "border-0 bg-white w-5 h-5",
    formMessageClasses: "",
    formFieldLabelClasses: "",
  },
  // Consent rows on the auth screens: a 20px box that turns red when the
  // required consent is missing, next to a regular-weight multi-line label.
  authConsent: {
    formItemClasses: "space-y-1",
    formCheckboxClasses:
      "border-border ring-offset-background focus-visible:ring-ring data-[state=checked]:bg-background-fill-primary-selected data-[state=checked]:border-background-fill-primary-selected data-[state=checked]:text-brand-white aria-invalid:border-brand-red-300 peer size-5 shrink-0 rounded-[4px] border focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
    formMessageClasses: "text-sm leading-[22px]",
    formFieldLabelClasses: "text-sm font-normal leading-[22px] text-text",
  },
};
export const FormCheckbox = <T extends FieldValues>({
  form,
  fieldName,
  fieldLabel,
  fieldDescription,
  layout = "default",
  required = false,
  onChangeCallback,
}: {
  form: UseFormReturn<T, unknown, T>;
  fieldName: Path<T>;
  fieldLabel?: ReactNode;
  fieldDescription?: ReactNode;
  layout?: keyof typeof layouts;
  required?: boolean;
  onChangeCallback?: () => void;
}) => {
  const t = useTranslations();

  const handleCheckboxChange = (
    field: ControllerRenderProps<T, Path<T>>,
    checked: string | boolean
  ) => {
    field.onChange(checked);
    onChangeCallback?.();
  };

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => (
        <>
          <FormItem className={layouts[layout].formItemClasses}>
            <div className="flex items-start gap-2">
              <FormControl>
                <Checkbox
                  {...field}
                  checked={field.value}
                  className={layouts[layout].formCheckboxClasses}
                  data-pw={`${field.name}-checkbox`}
                  onCheckedChange={(checked) => {
                    handleCheckboxChange(field, checked);
                  }}
                />
              </FormControl>
              <div>
                {fieldLabel && (
                  <FormLabel
                    className={cn(
                      { "space-x-2": required },
                      layouts[layout].formFieldLabelClasses
                    )}
                    data-pw={field.name}
                  >
                    <span>{fieldLabel}</span>
                    {required && <span>*</span>}
                  </FormLabel>
                )}
                {fieldDescription && (
                  <FormDescription>{fieldDescription}</FormDescription>
                )}
              </div>
            </div>
          </FormItem>
          <FormMessage className={layouts[layout].formMessageClasses} t={t} />
        </>
      )}
    />
  );
};
